import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import { COLORIZE_LANGUAGE_OPTIONS } from '@/lib/zybooks-api';
import type { OnMount } from '@monaco-editor/react';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { NodeViewContent, type ReactNodeViewProps } from '@tiptap/react';
import type { editor } from 'monaco-editor';
import { memo, useRef, useState } from 'react';
import { setupMonacoEditorOnMount } from '../monaco-editor-utils';

const EDITOR_OPTIONS = {
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on' as const,
    readOnly: false,
};

const parseHighlight = (highlight: string): string | null => {
    if (!highlight) return null;

    const lineNumbers: number[] = [];
    const segments = highlight.split(',');
    segments.forEach(segment => {
        if (segment.includes('-')) {
            const [start, end] = segment.split('-').map(Number);
            for (let i = start; i <= end; i++) {
                lineNumbers.push(i);
            }
        } else {
            if (Number.isInteger(Number(segment))) {
                lineNumbers.push(Number(segment));
            }
        }
    });
    return [...new Set(lineNumbers)].sort((a, b) => a - b).join(',');
};

const buildHighlightDecorations = (instance: editor.IStandaloneCodeEditor, highlightStr: string): editor.IModelDeltaDecoration[] => {
    const model = instance.getModel();
    const parsedHighlight = parseHighlight(highlightStr);
    if (!model || !parsedHighlight) return [];

    const lineCount = model.getLineCount();
    return parsedHighlight
        .split(',')
        .map(Number)
        .filter(n => Number.isInteger(n) && n >= 1 && n <= lineCount)
        .map(line => ({
            range: { startLineNumber: line, startColumn: 1, endLineNumber: line, endColumn: 1 },
            options: { isWholeLine: true, className: 'zycode-line-highlight' },
        }));
};

const ZyCodeNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, getPos, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const language = node.attrs.language || '';
    const highlight = node.attrs.highlight || '';
    const [highlightInput, setHighlightInput] = useState(highlight);
    const nobox = node.attrs.nobox || false;
    const numbering = node.attrs.numbering || false;
    const { id } = node.attrs;
    const [isFocused, setIsFocused] = useState(false);
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyCode', isFocused);
    const decorationsRef = useRef(new Map<editor.IStandaloneCodeEditor, editor.IEditorDecorationsCollection>());

    const codeContent = node.textContent || '';

    const getMonacoLanguage = (lang: string): string => {
        const languageMap: Record<string, string> = {
            cpp: 'cpp',
            c: 'c',
            csharp: 'csharp',
            java: 'java',
            python: 'python',
            javascript: 'javascript',
            js: 'javascript',
            typescript: 'typescript',
            html: 'html',
            css: 'css',
            sql: 'sql',
            php: 'php',
            kotlin: 'kotlin',
            matlab: 'matlab',
            xml: 'xml',
            json: 'json',
            yaml: 'yaml',
            shell: 'shell',
            asm: 'asm',
        };
        return languageMap[lang] || 'plaintext';
    };

    const syncLineHighlights = (highlightStr: string) => {
        for (const [instance, collection] of decorationsRef.current) {
            collection.set(buildHighlightDecorations(instance, highlightStr));
        }
    };

    const handleMonacoMount: OnMount = (instance, monaco) => {
        const monacoLanguage = getMonacoLanguage(language);
        const decorations = instance.createDecorationsCollection();
        decorationsRef.current.set(instance, decorations);

        const refreshSyntaxHighlighting = (attempt: number) => {
            const model = instance.getModel();
            if (!model) {
                if (attempt < 2) {
                    queueMicrotask(() => refreshSyntaxHighlighting(attempt + 1));
                }
                return;
            }

            monaco.editor.setModelLanguage(model, monacoLanguage);
            instance.layout();
            const value = model.getValue();
            model.setValue(value);
            decorations.set(buildHighlightDecorations(instance, highlight));
        };

        queueMicrotask(() => refreshSyntaxHighlighting(0));

        instance.onDidPaste(() => {
            const pos = getPos();
            if (typeof pos !== 'number') return;
            const highlightStr = editor.state.doc.nodeAt(pos)?.attrs.highlight || '';
            decorations.set(buildHighlightDecorations(instance, highlightStr));
        });

        instance.onDidDispose(() => {
            decorationsRef.current.delete(instance);
        });

        const shouldFocus = editor.storage.zyCode.pendingFocus;

        setupMonacoEditorOnMount(
            instance,
            () => setIsFocused(true),
            () => setIsFocused(false),
            shouldFocus,
        );
    };

    const handleCodeChange = (value: string | undefined) => {
        if (value === undefined) return;

        const pos = getPos();

        if (typeof pos === 'number') {
            const { state } = editor;
            const currentNode = state.doc.nodeAt(pos);
            if (currentNode) {
                const start = pos + 1;
                const end = pos + currentNode.nodeSize - 1;
                const tr = state.tr.replaceWith(start, end, state.schema.text(value || ' '));
                editor.view.dispatch(tr);
            }
        }
    };

    const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateAttributes({ language: e.target.value || null });
    };

    const handleHighlightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHighlightInput(e.target.value);
        const parsedHighlight = parseHighlight(e.target.value) || '';
        updateAttributes({ highlight: parsedHighlight || null });
        syncLineHighlights(parsedHighlight);
    };

    const handleBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateAttributes({ nobox: !e.target.checked });
    };

    const handleNumberingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateAttributes({ numbering: e.target.checked });
    };

    const customSettingsPanel = (
        <div
            contentEditable={false}
            className="rounded border-b border-gray-200 bg-gray-50 px-4 pt-6 pb-3 dark:border-gray-700 dark:bg-gray-800"
        >
            <fieldset
                disabled={isEditorReadonly}
                className="m-0 min-w-0 space-y-3 border-0 p-0"
            >
                <div className="flex items-start gap-4">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Language</label>
                        <select
                            value={language}
                            onChange={handleLanguageChange}
                            className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                        >
                            {COLORIZE_LANGUAGE_OPTIONS.map(option => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Highlight Lines</label>
                        <input
                            type="text"
                            value={highlightInput}
                            onChange={handleHighlightChange}
                            placeholder="1,4,5"
                            className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={!nobox}
                            onChange={handleBoxChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Box</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={numbering}
                            onChange={handleNumberingChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Line Numbering</span>
                    </label>
                </div>
            </fieldset>
        </div>
    );

    const lineNumbersClassName = numbering ? undefined : '[&_.line-numbers]:opacity-35';
    // Reserve left border width always so toggling Box only changes color, not layout.
    const editorShellClassName = `zycode-editor${nobox ? '' : ' zycode-box'}`;

    const renderMonacoEditor = (editorKey: string) => (
        <MonacoEditor
            key={editorKey}
            className={lineNumbersClassName}
            language={getMonacoLanguage(language)}
            value={codeContent}
            onChange={handleCodeChange}
            options={{ ...EDITOR_OPTIONS, readOnly: isEditorReadonly }}
            onMount={handleMonacoMount}
        />
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="zyCode"
            selected={selected}
            settingsPanel={customSettingsPanel}
            contentClassName="px-0 py-0"
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
            fullscreenContent={<div className={`h-full ${editorShellClassName}`}>{renderMonacoEditor('zycode-fullscreen')}</div>}
        >
            <NodeViewContent className="hidden" />
            <div
                className={`h-40 w-full min-w-0 max-w-full overflow-hidden ${editorShellClassName}`}
                contentEditable={false}
            >
                {renderMonacoEditor('zycode-inline')}
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyCodeNodeView);
